import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { staffApi, type StaffTicket } from '../../api/staff';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import './staff.css';

function chargeStatusLabel(status?: string | null) {
  return status === 'failed' ? 'Tarjeta rechazada' : 'Sin cobrar';
}

function formatEur(value?: number | string | null) {
  const amount = Number(value ?? 0);
  return `${amount.toFixed(2)} €`;
}

export function StaffPendingChargesPage() {
  const [tickets, setTickets] = useState<StaffTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryingId, setRetryingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const result = await staffApi.getMyTickets('resolved');
      const pending = result.tickets.filter(
        (ticket) => ticket.client_charge_status === 'pending' || ticket.client_charge_status === 'failed'
      );
      setTickets(pending);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar los cobros pendientes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRetry(ticketId: number) {
    setRetryingId(ticketId);
    setError('');
    try {
      const result = await staffApi.retryCharge(ticketId);
      if (result.clientChargeStatus === 'paid') {
        setTickets((prev) => prev.filter((ticket) => ticket.id !== ticketId));
      } else {
        setTickets((prev) =>
          prev.map((ticket) =>
            ticket.id === ticketId
              ? { ...ticket, client_charge_status: (result.clientChargeStatus as StaffTicket['client_charge_status']) ?? ticket.client_charge_status }
              : ticket
          )
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo reintentar el cobro');
    } finally {
      setRetryingId(null);
    }
  }

  return (
    <div>
      <div className="staff-page-header">
        <div>
          <h1>Cobros pendientes</h1>
          <p className="staff-tickets-summary">
            Tickets resueltos cuya tarjeta no se pudo cobrar automáticamente.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load}>
          Actualizar
        </Button>
      </div>

      {loading ? <p>Cargando…</p> : null}
      {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}

      {!loading && tickets.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>No hay cobros pendientes de tus tickets.</p>
      ) : null}

      <div className="staff-ticket-list">
        {tickets.map((ticket) => (
          <article key={ticket.id} className="staff-ticket-card">
            <div>
              <div className="staff-ticket-card__title-row">
                <h3>{ticket.customer_name || ticket.subject || `Ticket #${ticket.id}`}</h3>
              </div>
              <p>{ticket.subject}</p>
              <div className="staff-ticket-card__meta">
                <Badge tone={ticket.client_charge_status === 'failed' ? 'danger' : 'warning'}>
                  {chargeStatusLabel(ticket.client_charge_status)}
                </Badge>
                <Badge tone="neutral">{formatEur(ticket.client_charge_total)}</Badge>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
              <Button
                variant="secondary"
                size="sm"
                loading={retryingId === ticket.id}
                disabled={retryingId === ticket.id}
                onClick={() => void handleRetry(ticket.id)}
              >
                Reintentar cobro
              </Button>
              <Link to={`/staff/chat/${ticket.id}`}>
                <Button variant="ghost" size="sm">
                  Ver ticket
                </Button>
              </Link>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
